import { useCallback } from 'react';
import { Document } from '../types/document';
import { useDocuments } from '../../../../hooks/useDocuments';
import { useParams } from 'react-router-dom';

const MAX_FILES = 20;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

export const useDocumentUpload = (onUploadComplete?: () => void) => {
  const { uploadDocument } = useDocuments();
  const { id: propertyId } = useParams<{ id: string }>();

  const validateFiles = useCallback((files: File[]) => {
    files.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        throw new Error(`Le fichier ${file.name} dépasse la taille maximale de 10MB`);
      }

      const fileType = Object.keys(ACCEPTED_TYPES).find(type => file.type === type);
      if (!fileType) {
        throw new Error(`Le type de fichier ${file.type} n'est pas supporté`);
      }

      if (file.size === 0) {
        throw new Error(`Le fichier ${file.name} est vide`);
      }
    });
  }, []);

  const processFiles = useCallback(async (acceptedFiles: File[]) => {
    if (!propertyId) {
      throw new Error('Impossible d\'uploader des documents sans ID de propriété');
    }

    for (const file of acceptedFiles) {
      try {
        await uploadDocument(parseInt(propertyId), file, {
          document_type: 'other',
          description: file.name
        });

        if (onUploadComplete) {
          onUploadComplete();
        }
      } catch (error) {
        console.error(`Erreur lors de l'upload du document ${file.name}:`, error);
        throw error;
      }
    }
  }, [uploadDocument, propertyId, onUploadComplete]);

  const handleUpload = useCallback(async (files: File[]) => {
    try {
      validateFiles(files);
      await processFiles(files);
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : 'Une erreur est survenue';
    }
  }, [validateFiles, processFiles]);

  return { handleUpload, acceptedTypes: ACCEPTED_TYPES };
};
